import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
} from 'reactstrap';
import { toast } from 'react-toastify';

import { ODataGrid } from '../../../../../Components/OElements/ODataGrid';
import { getWarehouseResidueStock } from '../../../../../slices/warehouseResidueStock/thunk';
import { clearWarehouseResidueStock } from '../../../../../slices/warehouseResidueStock/reducer';
import {
	committedKgByStockSendKeyFromItems,
	guideWarehouseStockSendKey,
} from '../../utils/warehouseStockSendAvailability';

const formatKg = value => {
	const n = Number(value);
	if (!Number.isFinite(n)) return '-';
	return n.toLocaleString('es-CL', { maximumFractionDigits: 2 });
};

const isMongoObjectIdString = value =>
	typeof value === 'string' && /^[a-fA-F0-9]{24}$/.test(value);

const stockSelector = createSelector(
	state => state.WarehouseResidueStock,
	stock => ({
		stockList: stock.stockList,
		isLoadingList: stock.isLoadingList,
		stockMutationEpoch: stock.stockMutationEpoch,
	}),
);

/**
 * Input de kg por fila: solo activo si la fila está seleccionada.
 * Sin tocar fila, muestra "—". Al seleccionar, muestra el disponible por defecto;
 * el padre puede tener `override` tras blur.
 */
const StockKgToSendCell = React.memo(function StockKgToSendCell({
	rowId,
	cap,
	selected,
	override,
	onCommit,
}) {
	const [str, setStr] = useState('');

	useEffect(() => {
		if (!selected) {
			setStr('');
			return;
		}
		if (override !== undefined && Number.isFinite(Number(override))) {
			setStr(String(Number(override)));
			return;
		}
		setStr(String(Number(cap)));
	}, [selected, cap, override, rowId]);

	if (!selected) {
		return <span className='text-muted small'>—</span>;
	}

	const parseLoose = raw => {
		const n = parseFloat(String(raw).replace(',', '.'));
		return Number.isFinite(n) ? n : NaN;
	};

	const clamp = n => {
		let x = n;
		if (!Number.isFinite(x) || x <= 0) x = Number(cap);
		if (x > Number(cap)) x = Number(cap);
		return x;
	};

	return (
		<input
			type='number'
			className='form-control form-control-sm'
			style={{ maxWidth: '140px' }}
			min={0}
			step={0.01}
			max={cap}
			value={str}
			onKeyDown={e => {
				if (e.key === 'Enter') {
					e.preventDefault();
					e.stopPropagation();
				}
			}}
			onChange={e => {
				const v = e.target.value;
				setStr(v);
				if (v === '' || v === '-') {
					onCommit(rowId, undefined);
					return;
				}
				const n = parseLoose(v);
				if (!Number.isFinite(n)) return;
				if (n > 0 && n <= Number(cap)) {
					onCommit(rowId, n);
				}
			}}
			onBlur={() => {
				const n = clamp(parseLoose(str));
				setStr(String(n));
				onCommit(rowId, n);
			}}
		/>
	);
});

/**
 * Selector de slots de stock RESPEL para el generador (envío bodega peligrosos).
 * `establishmentId` y `warehouseId` vienen del formulario de la guía; `existingGuideItems`
 * alimenta el disponible efectivo (total API − kg ya en líneas stock-send).
 */
const WarehouseResidueStockPickerModal = ({
	isOpen,
	toggle,
	residueTypeId,
	onConfirm,
	establishmentId = null,
	warehouseId = null,
	existingGuideItems = [],
}) => {
	const dispatch = useDispatch();
	const { stockList, isLoadingList, stockMutationEpoch } =
		useSelector(stockSelector);

	const committedByKey = useMemo(
		() => committedKgByStockSendKeyFromItems(existingGuideItems),
		[existingGuideItems],
	);

	const stockListWithAvailability = useMemo(() => {
		return (stockList || [])
			.map(item => {
				const key = guideWarehouseStockSendKey(
					item.warehouse_id,
					item.residue_id,
				);
				const serverTotal = Number(item.kilograms_total) || 0;
				const committed = Number(committedByKey[key]) || 0;
				const available = Math.max(0, serverTotal - committed);
				return {
					...item,
					_id: key,
					kilograms_total_server: serverTotal,
					kilograms_in_guide: committed,
					kilograms_available: available,
				};
			})
			.filter(row => row.kilograms_available > 0);
	}, [stockList, committedByKey]);

	const canFetch =
		isOpen &&
		isMongoObjectIdString(residueTypeId) &&
		isMongoObjectIdString(establishmentId) &&
		isMongoObjectIdString(warehouseId);

	useEffect(() => {
		if (!isOpen) return;
		if (!canFetch) {
			dispatch(clearWarehouseResidueStock());
			return;
		}
		dispatch(
			getWarehouseResidueStock({
				respel_only: true,
				residue_type_id: residueTypeId,
				establishment_id: establishmentId,
				warehouse_id: warehouseId,
			}),
		);
	}, [
		isOpen,
		canFetch,
		residueTypeId,
		establishmentId,
		warehouseId,
		dispatch,
		stockMutationEpoch,
	]);

	const [selectionList, setSelectionList] = useState({});
	const [kgOverrides, setKgOverrides] = useState({});

	useEffect(() => {
		if (!isOpen) {
			setSelectionList({});
			setKgOverrides({});
		}
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen || stockMutationEpoch === 0) return;
		setSelectionList({});
		setKgOverrides({});
	}, [isOpen, stockMutationEpoch]);

	useEffect(() => {
		setKgOverrides(prev => {
			const next = { ...prev };
			for (const k of Object.keys(next)) {
				if (!selectionList[k]) delete next[k];
			}
			return next;
		});
	}, [selectionList]);

	const commitKg = useCallback((rowId, n) => {
		setKgOverrides(prev => {
			const next = { ...prev };
			if (n === undefined || !Number.isFinite(Number(n))) {
				delete next[rowId];
			} else {
				next[rowId] = Number(n);
			}
			return next;
		});
	}, []);

	const stockSelected = useMemo(() => {
		return stockListWithAvailability
			.filter(item => selectionList[item._id])
			.map(item => {
				const override = kgOverrides[item._id];
				const defaultKg = item.kilograms_available;
				const kg =
					override !== undefined && Number.isFinite(Number(override))
						? Number(override)
						: defaultKg;
				return {
					...item,
					kilograms_to_send: kg,
				};
			});
	}, [stockListWithAvailability, selectionList, kgOverrides]);

	const columns = useMemo(
		() => [
			{
				header: 'Bodega',
				accessorKey: 'warehouse.name',
				size: 'auto',
				Cell: ({ row }) =>
					row.original.warehouse?.name ||
					row.original.warehouse_name ||
					row.original.warehouse_id ||
					'-',
			},
			{
				header: 'Residuo',
				accessorKey: 'residue.name',
				size: 'auto',
				Cell: ({ row }) =>
					row.original.residue?.name ||
					row.original.residue_name ||
					row.original.residue_id ||
					'-',
			},
			{
				header: 'Kg en bodega',
				accessorKey: 'kilograms_total_server',
				size: 'auto',
				Cell: ({ row }) => formatKg(row.original.kilograms_total_server),
			},
			{
				header: 'En esta guía',
				accessorKey: 'kilograms_in_guide',
				size: 'auto',
				Cell: ({ row }) => formatKg(row.original.kilograms_in_guide),
			},
			{
				header: 'Disponible',
				accessorKey: 'kilograms_available',
				size: 'auto',
				Cell: ({ row }) => formatKg(row.original.kilograms_available),
			},
			{
				header: 'Kg a enviar',
				accessorKey: 'kilograms_to_send_draft',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				size: 'auto',
				Cell: ({ row }) => {
					const id = row.original._id;
					const cap = Number(row.original.kilograms_available) || 0;
					return (
						<StockKgToSendCell
							rowId={id}
							cap={cap}
							selected={!!selectionList[id]}
							override={kgOverrides[id]}
							onCommit={commitKg}
						/>
					);
				},
			},
			{
				header: 'Último movimiento',
				accessorKey: 'last_movement_date',
				dataType: 'datetime',
				size: 'auto',
				visible: false,
			},
		],
		[selectionList, kgOverrides, commitKg],
	);

	const handleConfirm = () => {
		if (stockSelected.length === 0) {
			toast.error('Debes seleccionar al menos un slot de stock para enviar.');
			return;
		}
		const invalidSlot = stockSelected.find(item => {
			const kg = Number(item.kilograms_to_send);
			const cap = Number(item.kilograms_available);
			return (
				!Number.isFinite(kg) ||
				kg <= 0 ||
				!Number.isFinite(cap) ||
				kg > cap
			);
		});
		if (invalidSlot) {
			toast.error(
				`Kilogramos inválidos para ${invalidSlot.residue?.name || 'el residuo seleccionado'}.`,
			);
			return;
		}

		const stockSlotsForPrefill = stockSelected.map(item => {
			const warehouseName =
				item.warehouse?.name || item.warehouse_name || '';
			return {
				_is_warehouse_stock_send: true,
				warehouse_id: item.warehouse_id,
				residue_id: item.residue_id,
				warehouse: item.warehouse,
				warehouse_name: warehouseName,
				residue: item.residue,
				kilograms_to_send: Number(item.kilograms_to_send),
				kilograms_total: Number(item.kilograms_total_server),
			};
		});

		onConfirm(stockSlotsForPrefill);
	};

	const selectedCount = Object.keys(selectionList).filter(
		k => selectionList[k],
	).length;

	return (
		<Modal
			isOpen={isOpen}
			toggle={toggle}
			size='xl'
			backdrop='static'
			centered
			className='border-0'
			modalClassName='modal fade zoomIn'
		>
			<ModalHeader toggle={toggle}>Agregar desde stock RESPEL</ModalHeader>
			<ModalBody>
				<p className='text-muted small mb-3'>
					Seleccione una o más filas. Los kilogramos a enviar se rellenan con el
					disponible; puede ajustarlos en la misma tabla antes de confirmar.
				</p>
				<ODataGrid
					columns={columns}
					data={stockListWithAvailability}
					getRowIdTable={originalRow => originalRow._id}
					selectAllMode='all'
					selection={{
						rowSelection: selectionList,
						setRowSelection: setSelectionList,
					}}
					isLoadingData={isLoadingList}
				/>
			</ModalBody>
			<ModalFooter className='gap-2'>
				<Button type='button' color='light' onClick={toggle}>
					Cancelar
				</Button>
				<Button
					type='button'
					color='primary'
					onClick={handleConfirm}
					disabled={selectedCount === 0}
				>
					<i className='ri-check-line me-1'></i>
					Confirmar
					{selectedCount > 0 ? ` (${selectedCount})` : ''}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default WarehouseResidueStockPickerModal;
